/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { PlaylistClip } from "types/Clip";
import { FADE_IN_CURVE } from "PhonosConstants";
import { millisecondsToSeconds } from "TimeHelper";
import { AudioEffect } from "types/AudioEffect";
import { EnhancedLogger } from "utilities/Logger";

export class FadeInEffect extends AudioEffect {
  private readonly gainNode: GainNode;

  constructor(
    audioContext: AudioContext,
    clip: PlaylistClip,
    playlistTime: number,
    protected fadeDuration: number,
    logger?: EnhancedLogger
  ) {
    super(audioContext, clip, playlistTime, logger);
    this.gainNode = this.audioContext.createGain();

    const seekTimeRTC = Math.max(playlistTime - clip.startTime, 0);

    if (seekTimeRTC < fadeDuration) {
      this.gainNode = this.audioContext.createGain();

      const curveDuration = fadeDuration - seekTimeRTC;
      const curvePercentage = curveDuration / fadeDuration;
      const appliedCurveLength = Math.max(
        curvePercentage * FADE_IN_CURVE.length,
        2
      );
      const appliedCurve = FADE_IN_CURVE.slice(
        FADE_IN_CURVE.length - appliedCurveLength,
        FADE_IN_CURVE.length
      );

      this.gainNode.gain.setValueCurveAtTime(
        appliedCurve,
        millisecondsToSeconds(clip.startTime + seekTimeRTC - playlistTime) +
          this.audioContext.currentTime,
        millisecondsToSeconds(curveDuration)
      );
    }
  }

  override hotswap(newClip: PlaylistClip): void {}

  override get inputNode(): AudioNode {
    return this.gainNode;
  }

  override get outputNode(): AudioNode {
    return this.gainNode;
  }

  disconnect(): void {
    this.gainNode.disconnect();
  }
}
